import { useContext, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useNavigate } from "react-router-dom";
import { MobXProviderContext } from "mobx-react";
import { RouterAddress } from "../../constants/RouterAddress";
import { ROLE_TYPE_PROVIDER } from "../../constants/RegisterConstants";

type ProviderSubscriptionDialogProps = {
  visible: boolean;
  featureName?: string;
  message?: string;
  details?: string[];
  showBillingButton?: boolean;
  onAccessUnlocked?: () => void;
  onHide: () => void;
};

const ProviderSubscriptionDialog = ({
  visible,
  featureName,
  message,
  details,
  showBillingButton = true,
  onAccessUnlocked,
  onHide,
}: ProviderSubscriptionDialogProps) => {
  const navigate = useNavigate();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const normalizedFeatureName = String(featureName || "").trim().toLowerCase();
  const isEmarFeature = normalizedFeatureName === "emar";
  const isAdlFeature = normalizedFeatureName === "adl" || normalizedFeatureName.includes("adl tracking");
  const [unlockEligible, setUnlockEligible] = useState(false);
  const [unlockExpiresAt, setUnlockExpiresAt] = useState<string | null>(null);
  const [loadingUnlockState, setLoadingUnlockState] = useState(false);
  const [activatingUnlock, setActivatingUnlock] = useState(false);
  const [unlockError, setUnlockError] = useState<string>("");
  const isProviderUser = authStore.getUserRoleType() === ROLE_TYPE_PROVIDER;
  const formattedUnlockExpiryDate = unlockExpiresAt
    ? new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(unlockExpiresAt))
    : "";

  useEffect(() => {
    let ignore = false;

    const loadUnlockState = async () => {
      if (!visible || !isProviderUser) {
        if (!ignore) {
          setUnlockEligible(false);
          setUnlockExpiresAt(null);
          setUnlockError("");
        }
        return;
      }

      const userId = authStore.getUserId();
      if (!userId) {
        return;
      }

      try {
        setLoadingUnlockState(true);
        setUnlockError("");
        await backendAPIStore.init();
        const subscriptionDetails = await backendAPIStore.getSubscription(userId, ROLE_TYPE_PROVIDER);
        if (ignore) {
          return;
        }
        setUnlockEligible(!!subscriptionDetails?.accessUnlockEligible);
        setUnlockExpiresAt(String(subscriptionDetails?.accessUnlockExpiresAt || "").trim() || null);
      } catch (error: any) {
        if (!ignore) {
          setUnlockEligible(false);
          setUnlockExpiresAt(null);
          setUnlockError(String(error?.message || "").trim());
        }
      } finally {
        if (!ignore) {
          setLoadingUnlockState(false);
        }
      }
    };

    void loadUnlockState();

    return () => {
      ignore = true;
    };
  }, [authStore, backendAPIStore, isProviderUser, visible]);

  const handleActivateUnlock = async () => {
    try {
      setActivatingUnlock(true);
      setUnlockError("");
      await backendAPIStore.init();
      await backendAPIStore.activateAccessUnlock();
      setUnlockEligible(false);
      if (typeof onAccessUnlocked === "function") {
        onHide();
        onAccessUnlocked();
        return;
      }
      window.location.reload();
    } catch (error: any) {
      setUnlockError(String(error?.message || "Unable to unlock provider access right now.").trim());
    } finally {
      setActivatingUnlock(false);
    }
  };

  return (
    <Dialog
      header="Subscription Required"
      visible={visible}
      style={{ width: "min(44rem, 94vw)" }}
      modal
      onHide={onHide}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {Array.isArray(details) && details.length ? (
          <div
            style={{
              border: isEmarFeature || isAdlFeature ? "1px solid #fdba74" : "1px solid #fde68a",
              background: isEmarFeature || isAdlFeature
                ? "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)"
                : "#fffbeb",
              borderRadius: 16,
              padding: "1rem 1.1rem",
            }}
          >
            <div style={{ color: "#92400e", fontWeight: 800, marginBottom: "0.35rem", fontSize: "1rem" }}>
              {isEmarFeature ? "Save time with eMAR" : isAdlFeature ? "Make daily ADL charting easier" : "Included with this feature"}
            </div>
            {isEmarFeature ? (
              <div style={{ color: "#9a3412", lineHeight: 1.6, marginBottom: "0.6rem", fontWeight: 600 }}>
                Keep the resident medication list and daily med-pass workflow together for your team.
              </div>
            ) : null}
            {isAdlFeature ? (
              <div style={{ color: "#9a3412", lineHeight: 1.6, marginBottom: "0.6rem", fontWeight: 600 }}>
                Turn the resident care plan into a simple daily ADL workflow for your team.
              </div>
            ) : null}
            <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "#78350f", lineHeight: 1.6 }}>
              {details.map((detail, index) => (
                <li key={`${featureName || "feature"}-detail-${index}`}>{detail}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <div style={{ color: "#334155", lineHeight: 1.6 }}>
          {message
            || (featureName
              ? `Subscription required for ${featureName}.`
              : "Subscription required for this feature.")}
        </div>
        {isProviderUser ? (
          <div
            style={{
              border: "1px solid #bfdbfe",
              background: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)",
              borderRadius: 16,
              padding: "1rem 1.1rem",
            }}
          >
            <div style={{ color: "#1d4ed8", fontWeight: 800, marginBottom: "0.35rem", fontSize: "1rem" }}>
              One-time 7-day access
            </div>
            <div style={{ color: "#1e3a8a", lineHeight: 1.6 }}>
              {unlockEligible
                ? "Use your one-time 7-day provider access unlock to keep working before you subscribe."
                : unlockExpiresAt
                  ? `Your one-time provider unlock was already used. It was active until ${formattedUnlockExpiryDate}.`
                  : "Your one-time 7-day provider access unlock has already been used."}
            </div>
            {unlockError ? (
              <div style={{ color: "#b91c1c", marginTop: "0.65rem", lineHeight: 1.5 }}>
                {unlockError}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2 justify-content-end">
          <Button
            type="button"
            label="Close"
            severity="secondary"
            rounded
            outlined
            onClick={onHide}
          />
          {isProviderUser && unlockEligible ? (
            <Button
              type="button"
              label={loadingUnlockState ? "Checking..." : "Unlock Access for 7 Days"}
              severity="info"
              rounded
              outlined
              loading={activatingUnlock}
              disabled={loadingUnlockState || activatingUnlock}
              onClick={() => {
                void handleActivateUnlock();
              }}
            />
          ) : null}
          {showBillingButton ? (
            <Button
              type="button"
              label="View Subscription"
              severity="warning"
              rounded
              onClick={() => {
                onHide();
                navigate(`${RouterAddress.BILLING}?tab=subscription`);
              }}
            />
          ) : null}
        </div>
      </div>
    </Dialog>
  );
};

export default ProviderSubscriptionDialog;
